/**
 * Pointer drag with corner snap for the panel header and the pill (spec section 6.2).
 *
 * Below a 4 px threshold a press is a click, not a drag. Past it the element follows the pointer via the
 * CSS `translate` property; on release it snaps to the corner whose quadrant holds its centre, and a
 * FLIP step animates the last distance (no animation under `prefers-reduced-motion`, enforced by the
 * `motion-reduce:transition-none` class on the element). Esc or pointercancel returns to the old corner.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { Corner } from './constants';

const DRAG_THRESHOLD_PX = 4;
const IGNORED_TARGETS = 'button,[role="radio"],[role="group"],[role="combobox"],input,textarea';

interface Options {
  onSnap: (corner: Corner) => void;
  /** The pill is itself a button and drags from anywhere on it. */
  dragFromButtons?: boolean;
}

export function cornerForPoint(x: number, y: number, width: number, height: number): Corner {
  const vertical = y < height / 2 ? 'top' : 'bottom';
  const horizontal = x < width / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
}

export function usePanelDrag<T extends HTMLElement>({ onSnap, dragFromButtons = false }: Options) {
  const ref = useRef<T>(null);
  const start = useRef<{ x: number; y: number; id: number } | null>(null);
  const moved = useRef(false);
  const suppressClick = useRef(false);
  const snapFrom = useRef<DOMRect | null>(null);
  const [offset, setOffset] = useState<{ dx: number; dy: number } | null>(null);
  const [snapTick, setSnapTick] = useState(0);

  const cancel = useCallback((): void => {
    start.current = null;
    moved.current = false;
    document.body.classList.remove('select-none');
    setOffset(null);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<T>): void => {
      if (event.button !== 0) return;
      const target = event.target as Element;
      if (!dragFromButtons && target.closest(IGNORED_TARGETS)) return;
      start.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
      moved.current = false;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [dragFromButtons],
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<T>): void => {
    const origin = start.current;
    if (!origin) return;
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    if (!moved.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    if (!moved.current) document.body.classList.add('select-none');
    moved.current = true;
    setOffset({ dx, dy });
  }, []);

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<T>): void => {
      if (!start.current) return;
      const el = ref.current;
      const wasDrag = moved.current;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      if (wasDrag && el) {
        const rect = el.getBoundingClientRect();
        snapFrom.current = rect;
        suppressClick.current = true;
        onSnap(
          cornerForPoint(rect.left + rect.width / 2, rect.top + rect.height / 2, window.innerWidth, window.innerHeight),
        );
        setSnapTick((n) => n + 1);
      }
      cancel();
    },
    [cancel, onSnap],
  );

  useEffect(() => {
    if (offset === null) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') cancel();
    };
    globalThis.addEventListener('keydown', onKey, true);
    return () => globalThis.removeEventListener('keydown', onKey, true);
  }, [offset, cancel]);

  // FLIP: animate from where the element was released to its new corner anchor.
  useLayoutEffect(() => {
    const from = snapFrom.current;
    const el = ref.current;
    if (!from || !el) return;
    snapFrom.current = null;
    const to = el.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    if (dx === 0 && dy === 0) return;
    el.style.transition = 'none';
    el.style.translate = `${dx}px ${dy}px`;
    el.getBoundingClientRect(); // Force a synchronous reflow so the start position commits before the transition.
    el.style.transition = '';
    el.style.translate = '';
  }, [snapTick]);

  /** For a pill: true once after a drag, so the click that ends the drag does not restore. */
  const consumeDragClick = useCallback((): boolean => {
    const was = suppressClick.current;
    suppressClick.current = false;
    return was;
  }, []);

  return {
    ref,
    dragging: offset !== null,
    style: offset ? { translate: `${offset.dx}px ${offset.dy}px` } : undefined,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: cancel },
    consumeDragClick,
  };
}
